import {useIntl} from 'react-intl'

const WIDTH_ICON = 35
const THUMB_SIZE = 40

export const RenderFilePreview = ({file}: {file: string}) => {
  const intl = useIntl()
  // Normalize and robustly extract extension:
  // - lowercases
  // - strips query/hash params
  // - takes the LAST segment after the dot (supports filenames with multiple dots)
  const cleaned = (file || '').toLowerCase()
  const noQuery = cleaned.split('?')[0].split('#')[0]
  const filename = noQuery.split('/').pop() || ''
  const extension = filename.includes('.') ? (filename.split('.').pop() as string) : ''

  // If we still couldn't detect an extension, show a generic icon
  if (!extension) {
    return (
      <img
        src='/media/tfi/files/txt.png'
        alt={intl.formatMessage({id: 'policies.detail.tab.docs.fileAlt', defaultMessage: 'file'})}
        width={WIDTH_ICON}
      />
    )
  }

  // Word
  const fileWordArr = ['doc', 'docx', 'docm', 'dot', 'dotx', 'dotm', 'rtf']

  // Excel
  const fileExcelArr = ['xls', 'xlsx', 'xlsm', 'xlsb', 'xlt', 'xltx', 'xltm', 'csv']

  // PowerPoint
  const filePptArr = ['ppt', 'pptx', 'pptm', 'pot', 'potx', 'potm', 'pps', 'ppsx', 'ppsm']

  // Images (extend list; support common iOS/HEIF and TIFF short form)
  const fileImageArr = [
    'jpg',
    'jpeg',
    'jpe',
    'jfif',
    'png',
    'gif',
    'bmp',
    'tif',
    'tiff',
    'svg',
    'webp',
    'ico',
    'heic',
    'heif',
  ]

  // Videos
  const fileVideoArr = ['mp4', 'mov', 'avi', 'wmv', 'flv', 'mkv', 'webm', 'mpeg', '3gp', 'ogg']

  // PDF
  const filePdfArr = ['pdf']

  // Audio
  const fileAudioArr = ['mp3', 'wav', 'aac', 'flac', 'ogg', 'wma', 'm4a', 'aiff', 'alac']

  // Text-ish
  const fileTextArr = ['txt', 'md', 'log', 'json', 'xml', 'csv']

  // Compressed
  const fileCompressedArr = ['zip', 'rar', '7z', 'tar', 'gz']

  if (fileWordArr.includes(extension))
    return <img src='/media/tfi/files/word.webp' alt={extension} width={WIDTH_ICON} />
  if (fileExcelArr.includes(extension))
    return <img src='/media/tfi/files/excel.webp' alt={extension} width={WIDTH_ICON} />
  if (filePptArr.includes(extension))
    return <img src='/media/tfi/files/powerpoint.webp' alt={extension} width={WIDTH_ICON} />

  // For images, show a real thumbnail (works for .jpeg/.jpg even with querystrings and uppercase)
  if (fileImageArr.includes(extension)) {
    return (
      <img
        src={file}
        alt={extension}
        width={THUMB_SIZE}
        height={THUMB_SIZE}
        style={{objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd'}}
        onError={(e) => {
          // Fallback to generic icon if image cannot be loaded
          const target = e.currentTarget as HTMLImageElement
          target.onerror = null
          target.src = '/media/tfi/files/txt.png'
          target.width = WIDTH_ICON
          target.height = undefined as any
          target.style.objectFit = ''
          target.style.borderRadius = ''
          target.style.border = ''
        }}
      />
    )
  }

  if (fileVideoArr.includes(extension))
    return <img src='/media/tfi/files/video.webp' alt={extension} width={WIDTH_ICON} />
  if (filePdfArr.includes(extension))
    return <img src='/media/tfi/files/pdf.png' alt={extension} width={WIDTH_ICON} />
  if (fileAudioArr.includes(extension))
    return <img src='/media/tfi/files/audio.webp' alt={extension} width={WIDTH_ICON} />
  if (fileTextArr.includes(extension))
    return <img src='/media/tfi/files/txt.png' alt={extension} width={WIDTH_ICON} />
  if (fileCompressedArr.includes(extension))
    return <img src='/media/tfi/files/rar.webp' alt={extension} width={WIDTH_ICON} />

  // Unknown → generic icon
  return <img src='/media/tfi/files/txt.png' alt={extension} width={WIDTH_ICON} />
}
